import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search, Calendar, User, ArrowRight, Tag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogListPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [currentPage, selectedCategory, searchQuery]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 12,
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(searchQuery && { search: searchQuery })
      });

      const response = await fetch(`${API_URL}/api/blog/articles?${params}`);
      const data = await response.json();

      setArticles(data.articles || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchArticles();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Shipping Blog & News | Shippzy</title>
        <meta
          name="description"
          content="Read the latest shipping news, international courier tips, customs guides, and industry updates from Shippzy. Expert advice for shipping from Canada to worldwide destinations."
        />
        <meta name="keywords" content="shipping blog, international shipping tips, courier news, customs guides, Shippzy blog" />
        <link rel="canonical" href="https://www.shippzy.ca/blog" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-red-600 via-red-700 to-orange-600 text-white py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="mb-4 md:mb-6">
                <div className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-wider mb-2">
                  SHIPPZY
                </div>
                <div className="text-xl md:text-3xl lg:text-4xl font-semibold tracking-wide">
                  Shipping Blog
                </div>
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-red-100 max-w-3xl mx-auto mb-6 md:mb-8 px-4">
                Expert tips, industry news, and helpful guides for international shipping from Canada
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-6 py-4 pr-12 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-orange-300 shadow-lg"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full transition-colors"
                  >
                    <Search size={20} />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </section>

        {/* Categories Filter */}
        <section className="py-4 md:py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setCurrentPage(1);
                }}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-red-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Articles {total > 0 && `(${total})`}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => {
                    setSelectedCategory(cat.name);
                    setCurrentPage(1);
                  }}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === cat.name
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-8 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-12 md:py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-4 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-base md:text-lg">Loading articles...</p>
                </div>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-8 md:py-12 px-4">
                <div className="max-w-md mx-auto">
                  <svg className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No Articles Yet</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-4">
                    We're currently working on exciting shipping guides and tips for you.
                  </p>
                  <p className="text-xs md:text-sm text-gray-500">
                    Check back soon for expert advice on international shipping!
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles.map((article, index) => (
                    <motion.article
                      key={article.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 group"
                    >
                      {/* Featured Image */}
                      {article.featured_image?.url && (
                        <Link to={`/blog/${article.slug}`} className="block overflow-hidden">
                          <img
                            src={article.featured_image.url}
                            alt={article.featured_image.alt || article.title}
                            className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </Link>
                      )}

                      <div className="p-6">
                        {/* Category Badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <Tag size={16} className="text-red-600" />
                          <span className="text-sm font-medium text-red-600">
                            {article.category}
                          </span>
                        </div>

                        {/* Title */}
                        <Link to={`/blog/${article.slug}`}>
                          <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors">
                            {article.title}
                          </h2>
                        </Link>

                        {/* Excerpt */}
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {article.excerpt}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <Calendar size={16} />
                            <span>{formatDate(article.published_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User size={16} />
                            <span>{article.author}</span>
                          </div>
                        </div>

                        {/* Read More Link */}
                        <Link
                          to={`/blog/${article.slug}`}
                          className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold group/link"
                        >
                          Read More
                          <ArrowRight
                            size={18}
                            className="group-hover/link:translate-x-1 transition-transform"
                          />
                        </Link>
                      </div>
                    </motion.article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-6 py-3 rounded-lg bg-white border-2 border-gray-300 text-gray-700 font-medium hover:border-red-600 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    <div className="flex gap-2">
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => setCurrentPage(i + 1)}
                          className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                            currentPage === i + 1
                              ? 'bg-red-600 text-white'
                              : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-red-600 hover:text-red-600'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-6 py-3 rounded-lg bg-white border-2 border-gray-300 text-gray-700 font-medium hover:border-red-600 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-8 md:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Your Source for International Shipping Knowledge
              </h2>
              <div className="grid md:grid-cols-2 gap-8 text-gray-700">
                <div>
                  <h3 className="text-xl font-semibold text-red-600 mb-3">What You'll Find Here</h3>
                  <ul className="space-y-2">
                    <li>✈️ International shipping tips and best practices</li>
                    <li>📦 Packaging and customs documentation guides</li>
                    <li>🌍 Country-specific shipping requirements</li>
                    <li>💡 Cost-saving strategies for international courier</li>
                    <li>📰 Industry news and carrier updates</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-600 mb-3">Expert Insights</h3>
                  <p className="mb-3">
                    Our blog provides practical advice for shipping to{' '}
                    <Link to="/shipping-to-india-from-canada" className="text-red-600 hover:underline">India</Link>,{' '}
                    <Link to="/shipping-canada-to-uk" className="text-red-600 hover:underline">UK</Link>,{' '}
                    <Link to="/shipping-canada-to-uae" className="text-red-600 hover:underline">UAE</Link>,{' '}
                    <Link to="/shipping-canada-to-pakistan" className="text-red-600 hover:underline">Pakistan</Link>,{' '}
                    <Link to="/shipping-canada-to-australia" className="text-red-600 hover:underline">Australia</Link>,{' '}
                    <Link to="/shipping-canada-to-philippines" className="text-red-600 hover:underline">Philippines</Link>,
                    and over 185 countries worldwide.
                  </p>
                  <p>
                    Whether you're shipping documents, parcels, luggage, or cargo, find helpful guides
                    to make your international shipping experience smooth and hassle-free.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogListPage;
